import { motion } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';
import { Sheet } from 'react-modal-sheet';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../spinner';

export const LUCKY_DRAW_BOXES = 6;
export const LUCKY_DRAW_REWARD_AION = 10;

const CONFETTI_COLORS = ['#D96B6B', '#4A7BD9', '#6FC734', '#E8A0A0'];

type Props = {
    isOpen: boolean;
    onClose: () => void;
    /**
     * Resolves the draw once the user picked a box. Return null to go back to
     * the pick step (e.g. the user rejected the claim tx).
     */
    onDraw: () => Promise<'win' | 'lose' | null>;
};

type Step = 'pick' | 'revealing' | 'win' | 'lose';

function Confetti({ gray }: { gray?: boolean }) {
    const pieces = useMemo(
        () =>
            Array.from({ length: 18 }, (_, i) => ({
                id: i,
                left: Math.random() * 100,
                top: Math.random() * 90,
                rotate: Math.random() * 180,
                color: gray
                    ? '#BDBDBD'
                    : CONFETTI_COLORS[i % CONFETTI_COLORS.length],
                delay: Math.random() * 0.3,
            })),
        [gray]
    );

    return (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
            {pieces.map((p) => (
                <motion.span
                    key={p.id}
                    className="absolute block h-3 w-1.5 rounded-[1px]"
                    style={{
                        left: `${p.left}%`,
                        top: `${p.top}%`,
                        background: p.color,
                        rotate: p.rotate,
                    }}
                    initial={{ opacity: 0, y: -30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: p.delay, duration: 0.5 }}
                />
            ))}
        </div>
    );
}

/** Full-screen blocker while the draw is being resolved (API + claim tx). */
function LoadingOverlay() {
    const { t } = useTranslation();

    return (
        <div
            className="fixed inset-0 flex flex-col items-center justify-center gap-3 bg-black/60 text-white"
            style={{ zIndex: 10000 }}
        >
            <Spinner className="size-10" />
            <p className="text-sm font-medium">
                {t('Opening your prize, please wait...')}
            </p>
        </div>
    );
}

function PickView({
    revealing,
    picked,
    onPick,
}: {
    revealing: boolean;
    picked: number | null;
    onPick: (index: number) => void;
}) {
    const { t } = useTranslation();

    return (
        <div className="px-4 pb-6 text-center">
            <h2 className="text-2xl font-medium leading-tight text-black">
                {t('Lucky draw')}
                <br />
                {t('to win an')}{' '}
                <span className="text-[#7B2D33]">{t('iPhone 18 Pro')}</span>
            </h2>
            <p className="mt-1 text-sm text-black/50">{t('Pick ONE to open')}</p>
            <div className="mt-6 grid grid-cols-3 gap-2.5">
                {Array.from({ length: LUCKY_DRAW_BOXES }, (_, i) => (
                    <motion.button
                        key={i}
                        whileTap={{ scale: 0.94 }}
                        animate={
                            revealing && picked === i
                                ? { rotate: [0, -8, 8, -8, 8, 0] }
                                : { rotate: 0 }
                        }
                        transition={{ duration: 0.5 }}
                        disabled={revealing}
                        onClick={() => onPick(i)}
                        className="cursor-pointer overflow-hidden rounded-xl"
                        style={{
                            opacity: revealing && picked !== i ? 0.5 : 1,
                        }}
                    >
                        <img
                            src="/images/ip_reward.png"
                            alt=""
                            className="block h-auto w-full"
                        />
                    </motion.button>
                ))}
            </div>
            <p className="mt-6 flex items-center justify-center gap-1 text-[11px] text-black/50">
                <span aria-hidden>ⓘ</span>
                {t('Every successful order wins a chance to get an iPhone 18 Pro!')}
            </p>
        </div>
    );
}

function WinView() {
    const { t } = useTranslation();

    return (
        <div className="relative px-4 pb-10 text-center">
            <Confetti />
            <h2 className="text-2xl font-semibold text-[#7B2D33]">
                {t('Congratulations!')}
            </h2>
            <motion.img
                src="/images/aion.png"
                alt="AION"
                className="relative mx-auto mt-2 w-72 max-w-full"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: 'spring', duration: 0.6, bounce: 0.4 }}
            />
            <p className="relative text-xs text-black">
                {t('You successfully claimed')}{' '}
                <span className="font-bold">
                    {LUCKY_DRAW_REWARD_AION} AION
                </span>
            </p>
        </div>
    );
}

function LoseView({ onClose }: { onClose: () => void }) {
    const { t } = useTranslation();

    return (
        <div className="relative px-4 pb-8 text-center">
            <Confetti gray />
            <h2 className="text-2xl font-semibold text-[#7B2D33]">
                {t('Better Luck Next Time!')}
            </h2>
            <motion.img
                src="/images/lucky_box.png"
                alt=""
                className="relative mx-auto mt-2 w-72 max-w-full"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
            />
            <p className="relative mt-2 text-xs text-black/60">
                {t('One more trade to get another')}
                <br />
                <span className="font-bold text-black">
                    {t('Lucky Draw iPhone 18 Pro')}
                </span>
            </p>
            <button
                onClick={onClose}
                className="relative mt-5 cursor-pointer rounded-full bg-black px-16 py-3 text-sm font-bold text-white active:scale-[0.98] transition-all"
            >
                {t('Yes')}
            </button>
        </div>
    );
}

export function LuckyDrawPopup({ isOpen, onClose, onDraw }: Props) {
    const [step, setStep] = useState<Step>('pick');
    const [picked, setPicked] = useState<number | null>(null);

    useEffect(() => {
        if (isOpen) {
            setStep('pick');
            setPicked(null);
        }
    }, [isOpen]);

    const pick = async (index: number) => {
        if (step !== 'pick') {
            return;
        }
        setPicked(index);
        setStep('revealing');
        const result = await onDraw();
        setStep(result ?? 'pick');
    };

    // The draw can't be dismissed until the user has seen the result.
    const canDismiss = step === 'win' || step === 'lose';
    const handleClose = () => {
        if (canDismiss) {
            onClose();
        }
    };

    return (
        <Sheet isOpen={isOpen} onClose={handleClose} detent="content">
            <Sheet.Container>
                <Sheet.Header>
                    <div className="flex items-center justify-center p-4">
                        <Sheet.DragIndicator />
                    </div>
                </Sheet.Header>
                <Sheet.Content disableDrag={!canDismiss}>
                    {(step === 'pick' || step === 'revealing') && (
                        <PickView
                            revealing={step === 'revealing'}
                            picked={picked}
                            onPick={pick}
                        />
                    )}
                    {step === 'win' && <WinView />}
                    {step === 'lose' && <LoseView onClose={onClose} />}
                </Sheet.Content>
            </Sheet.Container>
            <Sheet.Backdrop onTap={handleClose} />
            {step === 'revealing' && <LoadingOverlay />}
        </Sheet>
    );
}
